<template>
  <div class="ai-accounting-page">
    <div class="page-header">
      <div>
        <h2>AI记账</h2>
        <p>输入一句话，也可以附加付款截图、小票或订单截图。AI只生成草稿，确认后才写入交易记录。</p>
      </div>
    </div>

    <el-card class="input-card" shadow="never">
      <el-form label-position="top">
        <el-form-item label="记账描述">
          <el-input
            v-model="prompt"
            type="textarea"
            :rows="5"
            maxlength="1000"
            show-word-limit
            placeholder="例如：今天中午用中信卡在满东园吃饭 27 元；昨天收到工资 8000 到招商工资卡"
          />
        </el-form-item>
        <el-form-item label="图片">
          <el-upload
            :auto-upload="false"
            :limit="6"
            list-type="picture-card"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            accept="image/*"
          >
            <el-icon><Plus /></el-icon>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="analyzing" :disabled="!canAnalyze" @click="handleAnalyze">
            AI生成草稿
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card v-if="drafts.length" class="draft-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span>交易草稿</span>
          <el-button type="success" :loading="saving" @click="handleSaveAll">确认写入 {{ drafts.length }} 笔</el-button>
        </div>
      </template>

      <div class="draft-list">
        <div v-for="(draft, index) in drafts" :key="index" class="draft-item">
          <div class="draft-top">
            <strong>#{{ index + 1 }}</strong>
            <el-tag :type="draft.transaction_type === '收入' ? 'success' : draft.transaction_type === '支出' ? 'danger' : 'info'">
              {{ draft.transaction_type }}
            </el-tag>
            <span class="confidence">置信度 {{ Math.round((draft.confidence || 0) * 100) }}%</span>
          </div>

          <el-form label-position="top" class="draft-form">
            <el-form-item label="时间">
              <el-date-picker
                v-model="draft.transaction_date"
                type="datetime"
                value-format="YYYY-MM-DD HH:mm:ss"
                format="YYYY-MM-DD HH:mm"
                class="full-control"
              />
            </el-form-item>
            <el-form-item label="类型">
              <el-select v-model="draft.transaction_type" class="full-control">
                <el-option label="支出" value="支出" />
                <el-option label="收入" value="收入" />
                <el-option label="转账" value="转账" />
                <el-option label="借入" value="借入" />
                <el-option label="借出" value="借出" />
                <el-option label="收款" value="收款" />
                <el-option label="还款" value="还款" />
              </el-select>
            </el-form-item>
            <el-form-item label="金额">
              <el-input-number v-model="draft.amount" :precision="2" :min="0.01" class="full-control" />
            </el-form-item>
            <el-form-item label="主账户">
              <el-select v-model="draft.primary_account_id" filterable class="full-control">
                <el-option v-for="account in accounts" :key="account.id" :label="account.account_name" :value="account.id" />
              </el-select>
            </el-form-item>
            <el-form-item v-if="needsSecondaryAccount(draft)" label="次账户">
              <el-select v-model="draft.secondary_account_id" filterable class="full-control">
                <el-option v-for="account in accounts" :key="account.id" :label="account.account_name" :value="account.id" />
              </el-select>
            </el-form-item>
            <el-form-item v-if="draft.transaction_type === '支出'" label="支出大类">
              <el-select v-model="draft.expense_major_category_id" filterable clearable class="full-control">
                <el-option v-for="category in expenseMajorCategories" :key="category.id" :label="category.category_name" :value="category.id" />
              </el-select>
            </el-form-item>
            <el-form-item v-if="draft.transaction_type === '支出'" label="支出小类">
              <el-select v-model="draft.expense_minor_category_id" filterable clearable class="full-control">
                <el-option
                  v-for="category in expenseMinorByMajor(draft.expense_major_category_id)"
                  :key="category.id"
                  :label="category.category_name"
                  :value="category.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item v-if="draft.transaction_type === '收入'" label="收入大类">
              <el-select v-model="draft.income_major_category_id" filterable clearable class="full-control">
                <el-option v-for="category in incomeMajorCategories" :key="category.id" :label="category.category_name" :value="category.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="备注" class="wide-field">
              <el-input v-model="draft.description" type="textarea" :rows="2" />
            </el-form-item>
          </el-form>

          <el-alert
            v-if="draft.reason"
            class="reason"
            type="info"
            :closable="false"
            :title="draft.reason"
          />
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import api from '../api'

export default {
  name: 'AIAccounting',
  components: { Plus },
  setup () {
    const store = useStore()
    const prompt = ref('')
    const files = ref([])
    const drafts = ref([])
    const analyzing = ref(false)
    const saving = ref(false)
    const expenseMajorCategories = ref([])
    const expenseMinorCategories = ref([])
    const incomeMajorCategories = ref([])

    const accounts = computed(() => store.getters.activeAccounts)
    const canAnalyze = computed(() => prompt.value.trim() || files.value.length)

    const handleFileChange = (file, fileList) => {
      files.value = fileList.map(item => item.raw).filter(Boolean)
    }

    const handleFileRemove = (file, fileList) => {
      files.value = fileList.map(item => item.raw).filter(Boolean)
    }

    const handleAnalyze = async () => {
      analyzing.value = true
      try {
        const formData = new FormData()
        formData.append('prompt', prompt.value || '请根据图片生成记账草稿')
        files.value.forEach(file => formData.append('images', file))
        const response = await api.analyzeAIAccounting(formData)
        drafts.value = response.data.transactions || []
        if (!drafts.value.length) {
          ElMessage.warning('AI没有识别出可记账交易')
        } else {
          ElMessage.success(`生成 ${drafts.value.length} 笔草稿，请确认后写入`)
        }
      } finally {
        analyzing.value = false
      }
    }

    const handleSaveAll = async () => {
      saving.value = true
      try {
        for (const draft of drafts.value) {
          await api.createTransaction(toTransactionPayload(draft))
        }
        ElMessage.success(`已写入 ${drafts.value.length} 笔交易`)
        drafts.value = []
        prompt.value = ''
      } finally {
        saving.value = false
      }
    }

    const toTransactionPayload = (draft) => {
      return {
        transaction_date: draft.transaction_date,
        transaction_type: draft.transaction_type,
        amount: draft.amount,
        primary_account_id: draft.primary_account_id,
        secondary_account_id: draft.secondary_account_id || null,
        expense_major_category_id: draft.transaction_type === '支出' ? draft.expense_major_category_id || null : null,
        expense_minor_category_id: draft.transaction_type === '支出' ? draft.expense_minor_category_id || null : null,
        income_major_category_id: draft.transaction_type === '收入' ? draft.income_major_category_id || null : null,
        description: draft.description || '',
        merchant_id: 1,
        tag_id: 1,
        member_id: 1,
        member_amount: draft.amount,
        reimbursement_status_id: 1
      }
    }

    const needsSecondaryAccount = (draft) => {
      return ['转账', '借入', '借出', '收款', '还款'].includes(draft.transaction_type)
    }

    const expenseMinorByMajor = (majorId) => {
      if (!majorId) return expenseMinorCategories.value
      return expenseMinorCategories.value.filter(item => item.parent_id === majorId || item.major_category_id === majorId)
    }

    onMounted(async () => {
      await store.dispatch('fetchAccounts', { includeHidden: true })
      const [expenseMajor, expenseMinor, incomeMajor] = await Promise.all([
        api.getExpenseMajorCategories(),
        api.getExpenseMinorCategories(),
        api.getIncomeMajorCategories()
      ])
      expenseMajorCategories.value = expenseMajor || []
      expenseMinorCategories.value = expenseMinor || []
      incomeMajorCategories.value = incomeMajor || []
    })

    return {
      prompt,
      drafts,
      files,
      analyzing,
      saving,
      accounts,
      expenseMajorCategories,
      incomeMajorCategories,
      canAnalyze,
      handleFileChange,
      handleFileRemove,
      handleAnalyze,
      handleSaveAll,
      needsSecondaryAccount,
      expenseMinorByMajor
    }
  }
}
</script>

<style scoped>
.ai-accounting-page {
  min-height: 100%;
}

.page-header {
  margin-bottom: 18px;
}

.page-header h2 {
  margin: 0 0 6px;
  color: #263238;
  font-size: 26px;
}

.page-header p {
  margin: 0;
  color: #7b8794;
}

.input-card,
.draft-card {
  margin-bottom: 18px;
  border-radius: 14px;
}

.card-header,
.draft-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.draft-list {
  display: grid;
  gap: 16px;
}

.draft-item {
  padding: 14px;
  border: 1px solid #e5eaf3;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.draft-form {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.draft-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.full-control {
  width: 100%;
}

.wide-field {
  grid-column: span 2;
}

.confidence {
  color: #909399;
  font-size: 13px;
}

.reason {
  margin-top: 12px;
}

@media (max-width: 768px) {
  .draft-form {
    grid-template-columns: 1fr;
  }

  .wide-field {
    grid-column: span 1;
  }

  .card-header,
  .draft-top {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
